import { useEffect, useState, useCallback, useRef } from "react";
import { api, apiError, API } from "../../lib/api";
import { PageHeader } from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "../../components/ui/sheet";
import { Plus, PencilSimple, Trash, ImageSquare, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const IMG_BASE = API.replace(/\/api$/, "");
const EMPTY = { city_name: "", state_name: "", city_slug: "", active_status: 1 };

export default function Cities() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const cardRef = useRef(null);
  const bannerRef = useRef(null);
  const [uploadCity, setUploadCity] = useState(null);

  const load = useCallback(() => api.get("/cities").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ ...c }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.city_name) { toast.error("City name is required"); return; }
    try {
      const payload = { ...form, active_status: form.active_status ? 1 : 0 };
      if (editId) await api.put(`/cities/${editId}`, payload);
      else await api.post("/cities", payload);
      toast.success(`City ${editId ? "updated" : "added"}`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete ${c.city_name}?`)) return;
    await api.delete(`/cities/${c.id}`); toast.success("Deleted"); load();
  };
  const uploadImg = async (city, kind, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/cities/${city.id}/image?kind=${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${kind === "card" ? "City" : "Banner"} image updated`); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const imgUrl = (c, kind) => `${IMG_BASE}/api/public/city-image/${c.id}/${kind}?t=${c.updated_at || ""}`;

  return (
    <>
      <PageHeader title="Cities" subtitle="Manage homepage city cards & banners"
        actions={<Button onClick={openNew} className="rounded-sm gap-2" data-testid="add-city-btn"><Plus size={16} weight="bold" /> Add City</Button>} />
      <div className="p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="cities-grid">
          {rows.length === 0 && <p className="text-muted-foreground">No cities yet.</p>}
          {rows.map((c) => (
            <div key={c.id} className="border border-border rounded-lg overflow-hidden bg-card" data-testid={`city-card-${c.city_slug}`}>
              <div className="h-36 bg-secondary relative flex items-center justify-center">
                {c.city_image
                  ? <img src={imgUrl(c, "card")} alt={c.city_name} className="w-full h-full object-cover" />
                  : <ImageSquare size={40} className="text-muted-foreground/40" />}
                {!c.active_status && <span className="absolute top-2 left-2 bg-destructive text-white text-[10px] font-bold px-2 py-0.5 rounded">Inactive</span>}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading font-bold">{c.city_name}</div>
                    <div className="text-xs text-muted-foreground">{c.state_name} · /city/{c.city_slug}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-city-${c.city_slug}`}><PencilSimple size={15} /></Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => del(c)} data-testid={`del-city-${c.city_slug}`}><Trash size={15} /></Button>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <label className="flex-1">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadImg(c, "card", e.target.files?.[0]); e.target.value = ""; }} data-testid={`upload-card-${c.city_slug}`} />
                    <span className="flex items-center justify-center gap-1 text-xs border border-border rounded-sm py-1.5 cursor-pointer hover:bg-secondary"><UploadSimple size={13} /> Card img</span>
                  </label>
                  <label className="flex-1">
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => { uploadImg(c, "banner", e.target.files?.[0]); e.target.value = ""; }} data-testid={`upload-banner-${c.city_slug}`} />
                    <span className="flex items-center justify-center gap-1 text-xs border border-border rounded-sm py-1.5 cursor-pointer hover:bg-secondary"><UploadSimple size={13} /> Banner</span>
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="city-form">
          <SheetHeader><SheetTitle className="font-heading">{editId ? "Edit City" : "Add City"}</SheetTitle></SheetHeader>
          <div className="space-y-4 py-6">
            <div><Label className="label-caps">City Name</Label><Input data-testid="city-field-name" value={form.city_name} onChange={(e) => setForm({ ...form, city_name: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div><Label className="label-caps">State</Label><Input data-testid="city-field-state" value={form.state_name} onChange={(e) => setForm({ ...form, state_name: e.target.value })} className="rounded-sm mt-1.5" /></div>
            <div><Label className="label-caps">Slug (optional)</Label><Input data-testid="city-field-slug" value={form.city_slug} onChange={(e) => setForm({ ...form, city_slug: e.target.value })} placeholder="auto from name" className="rounded-sm mt-1.5" /><p className="text-xs text-muted-foreground mt-1">URL: /city/{form.city_slug || "auto"}</p></div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-primary" checked={!!form.active_status} onChange={(e) => setForm({ ...form, active_status: e.target.checked ? 1 : 0 })} data-testid="city-field-active" />
              <span className="text-sm">Active (show on storefront)</span>
            </label>
            {editId && <p className="text-xs text-muted-foreground">Upload images from the city card after saving.</p>}
          </div>
          <SheetFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm" onClick={save} data-testid="city-save">Save</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}
